import { ReactElement, useEffect, useRef, useState } from 'react'
import { useAtom } from 'jotai'
import { modalStatusAtom } from 'store/atoms'

import i18n from 'strings/i18n'
import { SlideInModalPortal } from 'syf-component-library/ui/patterns/SlideInModalPortal'
import ModalHeader from 'syf-component-library/ui/patterns/SlideInModalPortal/Header'
import getMFEConfig from 'helpers/getMFEConfig'
import { format, parse } from 'date-fns'
import { useAddAccountMutation } from 'services/AddAccountMutation'
import { SlideInModalLoader } from 'syf-component-library/ui/atoms'
import { UNPROCESSIBLE_CODE } from 'const/errorCodes'
import triggerAnalyticsEvent, {
  setAnalyticsDefaults
} from 'helpers/triggerAnalyticsEvent'
import strings from 'strings/lang/en'
import { useCreditAccounts } from 'const/syfApiQueries'
import { triggerOnCloseAnalytics } from 'helpers/triggerOnCloseAnalytics'
import useForm, { initialValue } from './helpers/useForm'
import { getErrorScreenType } from './helpers/getErrorScreenType'
import { renderScreen } from './helpers/renderScreen'
import { BusinessErrors, ScreenType } from './typings'
import {
  ADD_ACCOUNT,
  SNAPSHOT,
  getAnalyticsData
} from './helpers/getAnalyticsData'

const AddAccountModal = (): ReactElement => {
  const [screenType, setScreenType] = useState<ScreenType>('Default')
  const manageFormState = useForm()

  const headerRef = useRef(null)
  const { data: { accounts = [] } = {} } = useCreditAccounts()

  const mfeConfig = getMFEConfig('mfe')

  const { mutateAsync, isLoading } = useAddAccountMutation({
    config: mfeConfig
  })

  const [{ modalId, modalTriggerId }, updateModalStatus] =
    useAtom(modalStatusAtom)

  useEffect(() => {
    if (modalId === 'AddAccount') {
      const { setFormData, setFormError } = manageFormState
      setFormData(initialValue)
      setFormError(initialValue)
      setScreenType('Default')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalId])

  useEffect(() => {
    if (modalId === 'AddAccount') {
      const { pageView } = getAnalyticsData(screenType)
      const errorMessage =
        screenType === 'IncorrectInfo'
          ? strings.errors.incorrectInfoDescripton
          : ''
      setAnalyticsDefaults({
        PageKind: ADD_ACCOUNT,
        PageFunction: SNAPSHOT,
        PageSubFunction: ''
      })
      triggerAnalyticsEvent({
        ErrorMessage: errorMessage,
        ...pageView
      })
    }
  }, [screenType, modalId])

  const handleCloseModal = () => {
    updateModalStatus('', '')
    triggerOnCloseAnalytics()
  }

  const handleAddAccount = async () => {
    const { validateInput, isFormValid, formData, setFormData } =
      manageFormState
    validateInput()

    if (isFormValid.current) {
      const { accountNumber, identifier, dob } = formData
      const dateOfBirth = format(
        parse(dob, 'MM/dd/yyyy', new Date()),
        'yyyy-MM-dd'
      )

      const leadAccountId = accounts?.[0]?.info?.['cipher.accountId']

      try {
        const { success } = await mutateAsync({
          accountNumber,
          identifier,
          dateOfBirth,
          leadAccountId
        })
        if (success) {
          setScreenType('Success')
        }
      } catch (error) {
        const err = await error.response?.json()
        const status = error.response?.status as number
        let businessErrors: Array<BusinessErrors> = []
        if (status === UNPROCESSIBLE_CODE) {
          businessErrors = err.businessErrors
        }
        setScreenType(getErrorScreenType(status, businessErrors[0]?.code))
      } finally {
        setFormData(initialValue)
      }
    } else {
      triggerAnalyticsEvent({
        ErrorMessage: 'field error',
        MessageType: 'entered invalid information',
        pageName: 'error'
      })
    }
  }

  return (
    <SlideInModalPortal
      containerId="add-account-container-id"
      data-cy="slide-in-modal"
      escapeModalHandler={handleCloseModal}
      id="slide-in-modal"
      isOpen={modalId === 'AddAccount'}
      modalTriggerId={modalTriggerId}
      header={
        <ModalHeader
          closeModal={handleCloseModal}
          content={i18n({ labels: 'addAccount' }) as string}
          data-cy="add-account-modal-header"
          data-test="add-account-modal-header"
          dataReason="x"
          dataType="add account"
          headerRef={headerRef}
        />
      }
    >
      {isLoading ? (
        <SlideInModalLoader />
      ) : (
        renderScreen(
          screenType,
          manageFormState,
          handleAddAccount,
          handleCloseModal
        )
      )}
    </SlideInModalPortal>
  )
}

export default AddAccountModal
